import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";
import { api, session } from "./api";
import { setMoneyCurrency } from "./format";
import { useToast } from "./toast";

/** GET an API path, cached per active company. */
export function useApi<T>(path: string | null, opts: { refetchInterval?: number } = {}) {
  return useQuery({
    queryKey: [session.company, path],
    queryFn: () => api<T>(path!),
    enabled: path !== null,
    refetchInterval: opts.refetchInterval,
  });
}

/**
 * The active company's business rules — the defaults documents fall back on when nothing more
 * specific was agreed (payment terms, tax rate, approval limits).
 *
 * Returns `undefined` while loading, so read through a fallback rather than waiting for it.
 */
export function useCompanyRules(): CompanyRules | undefined {
  const list = useApi<{ id: string; settings: CompanyRules }[]>("/companies").data;
  return list?.find((c) => c.id === session.company)?.settings;
}

/** The subset of a company's settings other modules read. */
export interface CompanyRules {
  defaultTaxRate: number;
  defaultPaymentTermsDays: number;
  reserveStockOnConfirm: boolean;
  allowNegativeStock: boolean;
  autoPostInvoicesFromDeliveries: boolean;
  purchaseApprovalLimit: number | null;
  salesApprovalLimit: number | null;
  /** Yearly late-payment interest, charged to customers who pay late. 0 = none. */
  latePaymentInterestPct: number;
  /** Charged once per finance-charge invoice, for the trouble of chasing. 0 = none. */
  reminderFee: number;
  /** Days after the due date before interest starts running. */
  lateInterestGraceDays: number;
}

/** Runs a mutation, toasts the result and refreshes every cached query. */
export function useAction() {
  const qc = useQueryClient();
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = useCallback(
    async <T>(fn: () => Promise<T>, success?: string | ((r: T) => string)): Promise<T | undefined> => {
      setBusy(true);
      try {
        const r = await fn();
        if (success) toast(typeof success === "function" ? success(r) : success);
        await qc.invalidateQueries();
        return r;
      } catch (e) {
        toast((e as Error).message, "error");
        return undefined;
      } finally {
        setBusy(false);
      }
    },
    [qc, toast],
  );
  return { run, busy };
}

/** Form state for modals: values, setter, submit with error capture. */
export function useForm<T extends Record<string, unknown>>(initial: T) {
  const [values, setValues] = useState<T>(initial);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = <K extends keyof T>(k: K, v: T[K]) => setValues((s) => ({ ...s, [k]: v }));
  const bind = (k: keyof T) => ({
    value: (values[k] ?? "") as string | number,
    onChange: (e: { target: { value: string } }) => set(k, e.target.value as T[keyof T]),
  });
  const submit = (fn: (v: T) => Promise<unknown>) => async () => {
    setBusy(true);
    setError(null);
    try {
      await fn(values);
    } catch (e) {
      setError((e as Error).message);
      throw e;
    } finally {
      setBusy(false);
    }
  };
  return { values, set, bind, submit, error, busy, setValues };
}

/**
 * Show this page's amounts in `code` — a loan kept in another currency (ERP-92). Set while
 * rendering, so the first paint is already right; back to the company's when the page goes.
 */
export function useMoneyCurrency(code: string | null | undefined) {
  setMoneyCurrency(code);
  useEffect(() => () => setMoneyCurrency(null), []);
}
